import { useMemo } from 'react';

import { IconItem } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Toolbar } from '@/components/toolbar';
import { Z_INDEX } from '@/constants';
import { useOptionalLandingPageContext } from '@/pages/landingPage/landingPageProvider/LandingPageContext';
import { colors, Icon } from '@/utils';

type ToolbarForWidgetProps = {
  widgetId: string | undefined;
  toolbarActionList?: IconItem[];
  zIndex?: number;
  disabled?: boolean;
  usePortal?: boolean;
};

export const ToolbarForWidget = ({
  widgetId,
  toolbarActionList = [],
  zIndex = Z_INDEX.content.floating,
  disabled,
  usePortal = true,
}: ToolbarForWidgetProps) => {
  const { t } = useTranslation(['landingPage', 'common']);

  const landingPageContext = useOptionalLandingPageContext();
  const { fireEvent } = landingPageContext?.gridEventBus ?? {};
  const handleReplaceWidget = landingPageContext?.handleReplaceWidget;

  const widgetToolbarActions = useMemo<IconItem[]>(() => {
    // If no context or no widget id, just return whatever we already had
    if (!widgetId || !fireEvent || !handleReplaceWidget) {
      return toolbarActionList;
    }

    return [
      ...toolbarActionList,
      {
        label: t('switchWidgetContent'),
        id: uuidv4(),
        onClick: () => handleReplaceWidget(widgetId),
        Icon: Icon.RefreshCw,
      },
      {
        label: t('common:delete'),
        id: uuidv4(),
        onClick: () => fireEvent('onRemove', { widgetId }),
        Icon: Icon.X,
      },
    ];
  }, [toolbarActionList, widgetId, fireEvent, handleReplaceWidget, t]);
  if (widgetToolbarActions.length === 0) {
    return null;
  }
  return (
    <Toolbar
      className='keep-background'
      usePortal={usePortal}
      zIndex={zIndex}
      disabled={disabled}
      Icon={<Icon.Ellipsis size='large' style={disabled ? { color: colors.icon.disabled } : {}} />}
      actionList={widgetToolbarActions || []}
    />
  );
};
